import { HealthGorillaResourceType, HealthGorillaCodeSystem, HealthGorillaStructureDefinition, BillTo, HealthGorillaOrderBillTo, Gender } from '@caresend/types';
import { formatDateOfBirth } from '../formatters/date.js';
import { getStateAbbreviationByLocation, getCityByLocation, getPostalCodeByLocation, getCountryByLocation } from '../dataHelpers/location.js';
import { formatAddressUS, AddressFormatType } from '../formatters/location.js';
import { generateID } from './id.js';

/**
 * Useful when creating an literal reference within an FHIR resource
 * or when creating a literal reference that resides
 * in the 'contained' field of the resource
 *
 * https://www.hl7.org/fhir/references.html
 * {
 *  resourceType: 'PractitionerRole',
 *  contained: [{
 *    resourceType: 'Practitioner',
 *    id: '123'
 *    ...
 *  }]
 *  organization: {
 *    reference: 'Organization/organizationID'
 *  }
 * }
 *
 * * https://www.hl7.org/fhir/references.html
 * {
 *  resourceType: 'PractitionerRole',
 *  contained: [{
 *    resourceType: 'Practitioner',
 *    id: '123'
 *    ...
 *  }]
 *  practitioner: {
 *    reference: '#123'
 *  }
 * }
 */

const initLiteralIDReference = (idObj, resourceType) => {
  if (typeof idObj === 'string') {
    return {
      reference: resourceType ? `${resourceType}/${idObj}` : `#${idObj}`
    };
  }

  return {
    reference: resourceType ? `${resourceType}/${idObj.id}` : `#${idObj.id}`
  };
};
/**
 * Used to initialize a Coding object for a given system.
 */

const initCoding = (system, code) => ({
  system,
  code
});
/**
 * Initializes RequestGroup resource to be created in Health Gorilla
 */

const initRequestGroup = () => {
  const author = {
    reference: `${HealthGorillaResourceType.PRACTITIONER}/prescriberID`
  };
  const subject = {
    reference: `${HealthGorillaResourceType.PATIENT}/patientID`
  };
  const requestGroup = {
    resourceType: HealthGorillaResourceType.REQUEST_GROUP,
    meta: {
      profile: ['https://healthgorilla.com/fhir/StructureDefinition/hg-order']
    },
    contained: [],
    extension: [],
    status: 'active',
    intent: 'order',
    author,
    subject,
    action: []
  };
  return requestGroup;
};
/**
 * Maps CareSend Gender to HealthGorillaGender
 */

const getHealthGorillaGender = gender => {
  if (!gender) return 'unknown';

  switch (gender.toLowerCase()) {
    case Gender.MALE:
      return 'male';

    case Gender.FEMALE:
      return 'female';

    default:
      return 'other';
  }
};
/**
 * Maps CareSend BillTo to HealthGorillaOrderBillTo
 */


const getAccountBillTo = billTo => {
  switch (billTo) {
    case BillTo.PATIENT:
      return HealthGorillaOrderBillTo.PATIENT;

    case BillTo.CLIENT:
      return HealthGorillaOrderBillTo.SELF;

    case BillTo.INSURANCE:
      return HealthGorillaOrderBillTo.THIRD_PARTY;
  }
};
/**
 * Maps a CareSend User's name to an FHIR HumanName
 * https://www.hl7.org/fhir/datatypes.html#HumanName
 */


const initHealthGorillaUserName = info => ({
  use: 'usual',
  family: info.lastName,
  given: [info.firstName]
});
/**
 * Maps a CareSend Contact Info to an FHIR ContactPoint
 * https://www.hl7.org/fhir/datatypes.html#ContactPoint
 */

const initHealthGorillaContactPoint = (use, system, value) => ({
  use,
  system,
  value
});
/**
 * Maps a CareSend LocationObject to an FHIR Address
 */

const initHealthGorillaAddress = address => ({
  text: formatAddressUS(address, undefined, AddressFormatType.ONE_LINE),
  line: [formatAddressUS(address, AddressFormatType.ONE_LINE_STREET)],
  state: getStateAbbreviationByLocation(address),
  city: getCityByLocation(address),
  postalCode: getPostalCodeByLocation(address),
  country: getCountryByLocation(address)
});
/**
 * Maps a CareSend Insurance Provider's address to an FHIR Address
 */

const initHealthGorillaMailingAddress = address => ({
  line: [address.address1, ...(address.address2 ? [address.address2] : [])],
  state: address.state,
  city: address.city,
  postalCode: address.postalCode,
  country: address.country
});
/**
 * Code MR is associated with ID2. ID2 will prevent duplicate creation of patients within the Health Gorilla system
 * https://developer.healthgorilla.com/docs/fhir-restful-api-common#references
 */

const initHealthGorillaPatientIdentifier = patient => ({
  use: 'usual',
  type: {
    coding: [initCoding(HealthGorillaCodeSystem.V2_0203, 'MR')]
  },
  system: 'https://www.caresend.com',
  value: patient.id
});
/**
 * Mapping of a CareSend Patient User to an FHIR Patient for creation
 */

const initHealthGorillaPatient = patient => ({
  resourceType: HealthGorillaResourceType.PATIENT,
  name: [initHealthGorillaUserName(patient.info)],
  gender: getHealthGorillaGender(patient.info.gender),
  identifier: [initHealthGorillaPatientIdentifier(patient)],
  address: patient.info.address ? [initHealthGorillaAddress(patient.info.address)] : undefined,
  telecom: patient.info.phone ? [initHealthGorillaContactPoint('home', 'phone', patient.info.phone.number)] : undefined,
  birthDate: formatDateOfBirth(patient.info.dateOfBirth, 'YYYY-MM-DD')
});
/**
 * Creates new Identifier resource based on a Health Gorilla Code System
 */

const initHealthGorillaIdentifier = (system, value) => Object.assign(Object.assign({}, system && {
  system
}), {
  value
});
/**
 * Creates a Login user name for Health Gorilla Practitioner
 *
 * i.e. prescribermclocal
 */

const initHealthGorillaPractitionerLogin = info => ({
  url: HealthGorillaStructureDefinition.PRACTITIONER_LOGIN,
  valueString: `${info.firstName}${info.lastName}`.toLowerCase()
});
/**
 * Creates a login password for a Health Gorilla Practitioner
 *
 * i.e. prescriber1.dev@caresend.co
 */

const initHealthGorillaPractitionerPassword = info => ({
  url: HealthGorillaStructureDefinition.PRACTITIONER_PASSWORD,
  valueString: `${info.email}`.toLowerCase()
});
/**
 * Mapping of a CareSend Prescriber to an FHIR Practitioner for creation
 * 'meta' and 'extension' are specific to practitioner
 */

const initHealthGorillaPractitioner = (prescriber, practitioner) => {
  var _a, _b, _c;

  if (!((_a = prescriber.professionalInfo) === null || _a === void 0 ? void 0 : _a.npi)) return null;
  return Object.assign(Object.assign({
    resourceType: HealthGorillaResourceType.PRACTITIONER,
    id: prescriber.id,
    extension: [initHealthGorillaPractitionerLogin(prescriber.info), initHealthGorillaPractitionerPassword(prescriber.info)],
    identifier: [initHealthGorillaIdentifier(HealthGorillaCodeSystem.US_NPI, prescriber.professionalInfo.npi)],
    name: [initHealthGorillaUserName(prescriber.info)],
    telecom: [initHealthGorillaContactPoint('work', 'email', prescriber.info.email)]
  }, practitioner), {
    meta: Object.assign(Object.assign({}, practitioner === null || practitioner === void 0 ? void 0 : practitioner.meta), {
      profile: [...((_c = (_b = practitioner === null || practitioner === void 0 ? void 0 : practitioner.meta) === null || _b === void 0 ? void 0 : _b.profile) !== null && _c !== void 0 ? _c : []), HealthGorillaStructureDefinition.US_CORE_PRACTITIONER, HealthGorillaStructureDefinition.HG_PRACTITIONER]
    })
  });
};
/**
 * Takes a CareSend Prescriber and maps it to a Health Gorilla Practitioner.
 * Uses the Health Gorilla Practitioner and creates a role for an organization.
 */

const initHealthGorillaPractitionerRole = practitioner => ({
  resourceType: HealthGorillaResourceType.PRACTITIONER_ROLE,
  contained: [practitioner],
  practitioner: initLiteralIDReference(practitioner),
  active: true,
  code: [{
    coding: [initCoding(HealthGorillaCodeSystem.ADMINISTRATIVE_ROLE, 'provider'), initCoding(HealthGorillaCodeSystem.PRACTITIONER_ROLE, 'doctor')]
  }]
});
/**
 * Creates a Coverage resource for a patient where the patient is the subscriber and beneficiary
 */

const initPatientCoverage = (insurance, order, hgPatientID, payor) => {
  var _a;

  return {
    resourceType: HealthGorillaResourceType.COVERAGE,
    identifier: [initHealthGorillaIdentifier(undefined, insurance.number)],
    class: [{
      type: {
        coding: [initCoding(HealthGorillaCodeSystem.COVERAGE_CLASS, 'group')]
      },
      value: (_a = insurance.groupNumber) !== null && _a !== void 0 ? _a : ''
    }],
    subscriber: initLiteralIDReference(hgPatientID, HealthGorillaResourceType.PATIENT),
    beneficiary: initLiteralIDReference(hgPatientID, HealthGorillaResourceType.PATIENT),
    status: 'active',
    relationship: {
      coding: [initCoding(HealthGorillaCodeSystem.SUBSCRIBER_RELATIONSHIP, 'self')]
    },
    payor: [initLiteralIDReference(payor, HealthGorillaResourceType.ORGANIZATION)],
    order
  };
};
/**
 * Creates a new Coverage resource for a patient when the patient is not the subscriber
 */

const initPatientBeneficiaryCoverage = (insurance, order, hgPatientID, relatedPerson, payor) => {
  var _a, _b;

  const relationshipCoding = (_a = relatedPerson.relationship) === null || _a === void 0 ? void 0 : _a[0];
  if (!relationshipCoding) throw Error('No relationship defined for insurance');
  return {
    resourceType: HealthGorillaResourceType.COVERAGE,
    identifier: [initHealthGorillaIdentifier(undefined, insurance.number)],
    contained: [relatedPerson],
    status: 'active',
    subscriber: initLiteralIDReference(relatedPerson),
    beneficiary: initLiteralIDReference(hgPatientID, HealthGorillaResourceType.PATIENT),
    relationship: relationshipCoding,
    class: [{
      type: {
        coding: [initCoding(HealthGorillaCodeSystem.COVERAGE_CLASS, 'group')]
      },
      value: (_b = insurance.groupNumber) !== null && _b !== void 0 ? _b : ''
    }],
    payor: [initLiteralIDReference(payor, HealthGorillaResourceType.ORGANIZATION)],
    order
  };
};
/**
 * Creates a RelatedPerson resource for a Patient's insurance policy holder.
 * Will be contained in the Coverage resource.
 */

const initRelatedPerson = (beneficiaryID, subscriber) => ({
  resourceType: HealthGorillaResourceType.RELATED_PERSON,
  id: generateID(),
  patient: initLiteralIDReference(beneficiaryID, HealthGorillaResourceType.PATIENT),
  relationship: [{
    coding: [initCoding(HealthGorillaCodeSystem.SUBSCRIBER_RELATIONSHIP, subscriber.relationship)]
  }],
  name: [initHealthGorillaUserName(subscriber)],
  telecom: [initHealthGorillaContactPoint('home', 'phone', subscriber.phone.number)],
  address: [initHealthGorillaAddress(subscriber.address)]
});
/**
 * Determines the type of Coverage to create based on the presence of a policy holder.
 * If there is no policy holder, the Coverage is a Patient Coverage.
 * If there is a policy holder, the Coverage is a Patient Beneficiary Coverage.
 */

const initializeHealthGorillaPatientCoverage = (insuranceIndex, insurance, hgPatientID, payor) => {
  let hgCoverage;
  const order = insuranceIndex + 1;

  if (!insurance.policyHolderInfo) {
    hgCoverage = initPatientCoverage(insurance, order, hgPatientID, payor);
  } else {
    const hgRelatedPerson = initRelatedPerson(hgPatientID, insurance.policyHolderInfo);
    hgCoverage = initPatientBeneficiaryCoverage(insurance, order, hgPatientID, hgRelatedPerson, payor);
  }

  return hgCoverage;
};
/**
 * Initialize the Insurance Provider Organizatino resource.
 */

const initHealthGorillaInsuranceProvider = provider => ({
  resourceType: HealthGorillaResourceType.ORGANIZATION,
  active: true,
  type: [{
    coding: [initCoding(HealthGorillaCodeSystem.ORGANIZATION_TYPE, 'ins')]
  }],
  name: provider.name,
  telecom: [initHealthGorillaContactPoint('work', 'phone', provider.phone.number)],
  address: [initHealthGorillaMailingAddress(provider.address)]
});
/**
 * Initialize the Account.guarantor reference.
 * Currently, we only support Patient as the guarantor.
 */

const initAccountGuarantor = (billTo, userID) => {
  const guarantor = [];

  switch (billTo) {
    case HealthGorillaOrderBillTo.PATIENT:
      guarantor.push({
        party: initLiteralIDReference(userID, HealthGorillaResourceType.PATIENT)
      });
      break;
    // We currently don't support Guarantor
    // case HealthGorillaOrderBillTo.GUARANTOR:
    //   guarantor.push({
    //     party: initLiteralIDReference(userID),
    //   });
    //   break;
  }

  return guarantor;
};
/**
 * Initialize the Account.coverage reference when BillTo.INSURANCE.
 */


const initAccountCoverage = (coverageID, priority) => {
  const coverage = [];
  coverage.push({
    coverage: initLiteralIDReference(coverageID),
    priority
  });
  return coverage;
};
/**
 * Initializes the data that will determine if the Account will have a guarantor or coverage.
 * If the billTo is PATIENT, the guarantorID will be set.
 * If the billTo is INSURANCE, the coverageInfo will be set.
 */


const initAccountInitializerData = (billTo, patient, coverage) => {
  var _a;

  const coverageInfo = billTo === BillTo.INSURANCE && coverage && coverage.id && coverage.order ? {
    coverageID: coverage.id,
    priority: (_a = coverage.order) !== null && _a !== void 0 ? _a : 1
  } : undefined;
  const guarantorID = billTo === BillTo.PATIENT ? patient.id : undefined;
  return {
    billTo,
    coverageInfo,
    guarantorID
  };
};
/**
 * Initializes the Account resource.
 * If the billTo is PATIENT, the guarantorID will be set.
 * If the billTo is INSURANCE, the coverageInfo will be set.
 */

const initHealthGorillaAccount = ({
  billTo,
  guarantorID,
  coverageInfo
}) => {
  const billToCode = getAccountBillTo(billTo);

  if (billToCode === HealthGorillaOrderBillTo.PATIENT && !guarantorID) {
    throw Error(`Guarantor ID is required for Patient ${billToCode} bill to`);
  }

  if (billToCode === HealthGorillaOrderBillTo.THIRD_PARTY && !coverageInfo) {
    throw Error(`Coverage Info is required for Patient ${billToCode}} bill to`);
  }

  const guarantor = guarantorID ? initAccountGuarantor(billToCode, guarantorID) : [];
  const coverage = coverageInfo ? initAccountCoverage(coverageInfo.coverageID, coverageInfo.priority) : [];
  return Object.assign(Object.assign({
    resourceType: HealthGorillaResourceType.ACCOUNT,
    id: generateID(),
    status: 'active',
    meta: {
      profile: [HealthGorillaStructureDefinition.HG_ORDER_ACCOUNT]
    },
    type: {
      coding: [initCoding(HealthGorillaCodeSystem.ORDER_BILL_TO, billToCode)]
    }
  }, guarantor.length > 0 && {
    guarantor
  }), coverage.length > 0 && {
    coverage
  });
};
/**
 * Will be used to initialze the 'User.healthGorillaInfo' on a CareSend User
 * once a new resource is created in the Health Gorilla FHIR Server
 */

const initHealthGorillaInfo = resourceID => ({
  resourceID
});

export { initAccountInitializerData, initCoding, initHealthGorillaAccount, initHealthGorillaAddress, initHealthGorillaContactPoint, initHealthGorillaIdentifier, initHealthGorillaInfo, initHealthGorillaInsuranceProvider, initHealthGorillaMailingAddress, initHealthGorillaPatient, initHealthGorillaPatientIdentifier, initHealthGorillaPractitioner, initHealthGorillaPractitionerLogin, initHealthGorillaPractitionerPassword, initHealthGorillaPractitionerRole, initHealthGorillaUserName, initLiteralIDReference, initPatientBeneficiaryCoverage, initPatientCoverage, initRelatedPerson, initRequestGroup, initializeHealthGorillaPatientCoverage };
